import { Toast } from 'radix-ui';
import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useLocation, useViewTransitionState } from 'react-router';

import DemmiToasts from '@components/toast/Toast';
import Toolbar from '@components/toolbar/Toolbar';
import UserMenu from '@components/userMenu/UserMenu';
import { useAppDispatch, useAppSelector } from '@core/hooks/appHooks';
import { NetworkService } from '@core/services/networkService/networkService';
import { getViewFromPath } from '@core/services/routingService';
import { selectVendor, setVendor } from '@core/store/appSlice/appSlice';
import {
  selectVendorUser,
  setVendorUser,
} from '@core/store/userSlice/userSlice';
import DemmiButton from '@demmi-ui/Button';
import * as faSolid from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { demmiRequest } from '@helpers/app.helper';
import { useAuth } from '@providers/authProvider';
import { Logger } from '@subhanhabib/demmilib';

import { auth } from '../../firebase';
import LoadingGlobal from '../loadingIntro/LoadingGlobal';
import VendorWrapperHeader from './_header';
import VendorWrapperMobileMenu from './_mobileMenu';

const VendorWrapper: React.FC<PropsWithChildren> = ({ children }) => {
  const CSSBlock = 'vendor-wrapper';
  const { pathname, search } = useLocation();
  const isTransitioning = useViewTransitionState(
    { pathname, search }, // current location as 'to' parameter
    { relative: 'route' }, // optional opts parameter
  );

  const user = auth.currentUser;
  const vendorUser = useAppSelector(selectVendorUser);
  const vendor = useAppSelector(selectVendor);
  const dispatch = useAppDispatch();
  const { logOut } = useAuth();
  const [isMobileMenuActive, setIsMobileMenuActive] = useState(false);

  useEffect(() => {
    if (user && !vendorUser) {
      Logger({ messages: ['useEffect'], objs: { user } }, VendorWrapper);
      demmiRequest(NetworkService.Vendors.getVendorUser(user?.uid)).then(f =>
        dispatch(setVendorUser(f)),
      );
    }
  }, [user, vendorUser]);

  useEffect(() => {
    if (!vendor && vendorUser)
      demmiRequest(NetworkService.Vendors.getVendor(vendorUser?.vendorID)).then(
        f => dispatch(setVendor(f)),
      );
  }, [vendor, vendorUser]);

  return (
    <>
      <VendorWrapperMobileMenu
        CSSBlock={CSSBlock}
        isMobileMenuActive={isMobileMenuActive}
        setIsMobileMenuActive={setIsMobileMenuActive}
      />

      <div
        className={`${CSSBlock} ${isMobileMenuActive ? `${CSSBlock}--mobile-menu-active` : ''}`}>
        <div className={`${CSSBlock}__background`} />

        <VendorWrapperHeader
          setIsMobileMenuActive={setIsMobileMenuActive}
          isMobileMenuActive={isMobileMenuActive}
        />

        {vendor ? (
          <>
            <div
              className={`${CSSBlock}__content ${CSSBlock}--${getViewFromPath(
                pathname,
              )} ${isTransitioning ? `${CSSBlock}__content--transitioning` : ''}`}>
              {children}
            </div>
            <div className={`${CSSBlock}__toolbar`}>
              <Toolbar />
            </div>

            <UserMenu />
          </>
        ) : (
          <div className={`${CSSBlock}__content`}>
            <div
              className={`${CSSBlock}__error 
                ${isTransitioning ? `${CSSBlock}__error--transitioning` : ''}`}>
              <div className={`${CSSBlock}__error-title`}>
                Something went wrong
              </div>
              <div className={`${CSSBlock}__error-subtitle`}>
                We&apos;re sorry for the inconvenience.
              </div>
              <DemmiButton
                text="Logout"
                onClick={() => logOut(() => dispatch({ type: 'LOGOUT' }))}
              />
            </div>
          </div>
        )}

        <LoadingGlobal />

        <div
          className={`${CSSBlock}__foreground`}
          onClick={() => setIsMobileMenuActive(false)}>
          <span className={`${CSSBlock}__foreground-icon`}>
            <FontAwesomeIcon icon={faSolid.faLeft} />
          </span>
        </div>
      </div>

      <Toast.Provider>
        <DemmiToasts />
      </Toast.Provider>
      <div id="modal-container" />
      <div id="notifications-panel" />
      <div id="demmi-support-panel" />
    </>
  );
};

export default VendorWrapper;
